













































































































import Vue from 'vue'
import Component from 'vue-class-component'
import {Getter, Mutation, State} from 'vuex-class'
import {MessageListModes, MutationTypes} from '@/modules/i18n/store/mutations'
import {notify} from '@/modules/core/notification';
import MessageTranslation from '@/modules/i18n/model/MessageTranslation'
import LocaleSelectComponent from '@/modules/i18n/components/LocaleSelect.vue'
import Log from '@/modules/log'
import Editor from '@/components/Editor.vue'
import {Config} from '@/modules/core/config'
import Locale from '@/modules/i18n/model/Locale';
import Message from '@/modules/i18n/model/Message';
import {VTextField} from 'vuetify/lib';
import {Ref} from 'vue-property-decorator';
import { default as TinyMCE } from '@tinymce/tinymce-vue'

@Component({
    components: {
        'locale-select': LocaleSelectComponent,
        Editor, TinyMCE,
    },
})
export default class MessageDetailComponent extends Vue {
    public $refs: Vue['$refs'] & {
        keyField: {
            focus (): void,
        },
    }

    @State(state => state.config)
    public config: Config

    /**
     * Localismo actual seleccionado por el usuario en la lista de traducciones
     */
    @Getter('i18n/currentLocale')
    public locale: Locale | null

    /**
     * Modo actual de la lista, crear, editar, eliminar
     */
    @State(state => state.i18n.messageList.mode)
    public mode: MessageListModes

    /**
     * Mutación que permite establecer el modo de operación de la lista de
     * mensajes (crear, editar, eliminar)
     */
    @Mutation(`i18n/${MutationTypes.SetLocaleMessageMode}`)
    public setMode: (payload: { mode: MessageListModes }) => void

    /**
     * Mutación que permite establecer el mensaje actualmente en edición
     */
    @Mutation(`i18n/${MutationTypes.SetMessageListSelectedMessage}`)
    public selectMessage: (payload: { message: Message | null }) => void

    @Getter('i18n/localeMessage')
    public localeMessage: (locale: string, key: string) => Message | null

    @Mutation(`i18n/${MutationTypes.SetMessageInDraft}`)
    public setMessageInDraft: (payload: { message: Message }) => void

    @Mutation(`i18n/${MutationTypes.UpdateCurrentMessageField}`)
    public updateField: (payload: { path: string, value: any }) => void

    /**
     * Referecia al campo de texto de la clave del mensaje. Referencia utilizada
     * para dar el foco al campo cuando el usuario pulsa el botón
     * "Guardar y continuar"
     */
    @Ref('keyField')
    public keyField: {
        focus (): void,
    }

    public messageDetailLocale: Locale | null = null
    public operationError = ''
    public saving = false
    public errorMessages: string[] = []
    public disableInputs = false

    /**
     * Indica se se ha activado el modo "único idioma" en la configuración
     * del panel.
     */
    get singleLanguage (): boolean {
        return this.config.get('i18n.singleLanguage', false)
    }

    get translation (): MessageTranslation {
        return this.$store.getters['i18n/currentMessageTranslation'](this.currentLocale!.identifier)
    }

    private lastCardTitle = ''
    private lastMessage = null

    /**
     * Mensaje actualmente seleccionado por el usuario
     */
    @State(state => state.i18n.messageList.selectedMessage)
    public message: Message

    get richFormat (): boolean {
        return this.message.richFormat
    }

    set richFormat (richFormat: boolean) {
        this.updateField({path: 'richFormat', value: richFormat})
    }

    /**
     * Permite obtener el localismo actual
     */
    get currentLocale () {
        if (this.messageDetailLocale) {
            return this.messageDetailLocale
        }

        return this.locale
    }

    /**
     * Titulo de la tarjeta
     *
     * Genera un título para mostrar al usuario dependiendo de si es edición
     * o creación de un mensaje nuevo.
     */
    get cardTitle () {
        switch (this.mode) {
            case MessageListModes.Editing:
                this.lastCardTitle = `Editar ${this.singleLanguage ? 'texto' : 'traducción'}`
                break
            case MessageListModes.Creating:
                this.lastCardTitle = `Crear ${this.singleLanguage ? 'texto' : 'traducción'}`
                break
        }

        return this.lastCardTitle
    }

    get isEdit (): boolean {
        return this.mode === MessageListModes.Editing
    }

    get cardTextHeight () {
        return this.$vuetify.breakpoint.lgAndDown ? '446px' : '500px'
    }


    public updateTranslationField (path: string, value: any) {
        this.$store.commit(`i18n/${MutationTypes.UpdateCurrentMessageTranslationsField}`, {
            locale: this.currentLocale!.identifier, path, value,
        })
    }

    /**
     * Cancela el modal actual
     */
    public cancel (): void {
        this.$validator.reset()

        this.setMode({mode: MessageListModes.None})
        this.selectMessage({message: new Message()})
        this.messageDetailLocale = null

        Log.info('El usuario ha cerrado la pantalla de edición de la traducción', {module: 'i18n'})
    }

    /**
     * Guardar
     *
     * Método invocado al pulsar sobre el botón de guardar, crea un nuevo mensaje
     * o edita uno existente según el modo el que se encuentre el componente
     */
    public async save (saveAndContinue = false) {
        // No continuar si el formulario no pasa las validaciones
        if (!await this.$validator.validateAll()) {
            return
        }

        this.operationError = ''
        this.saving = true

        this.isEdit ? await this.update() : await this.createMessage()

        const mode = saveAndContinue ? MessageListModes.Creating : MessageListModes.None
        this.setMode({mode})

        if (saveAndContinue) {
            await this.$store.dispatch('i18n/buildNewMessage')
            this.$nextTick(() => this.keyField.focus())
        }

        // Se muestra una notificación de que el proceso se ha realizado correctamtne
        notify('Mensaje guardado correctamente')
        this.saving = false
    }

    /**
     * Crear mensaje
     *
     * Ejecuta una acción del store para crear un nuevo mensaje en el servidor
     * y registrarlo en el estado de la aplicación
     */
    private async createMessage () {
        const message = this.message
        await this.$store.dispatch('i18n/createLocaleMessage', {
            message,
        })
    }

    /**
     * Actualizar mensaje
     *
     * Comportamiento similar create() solo que este sobre-escribe la
     * información de un mensaje.
     */
    private async update () {
        await this.$store.dispatch('i18n/updateLocaleMessage', this.message)
    }

    /**
     * Propiedad conmutada que indica si el mensaje puede ser guardado
     */
    get canSave (): boolean {
        // if (this.mode === MessageListModes.Editing) {
        //     const message = this.localeMessage(this.locale, this.message.key)
        //     return message!.content !== this.message.content
        // }

        return true
    }

    /**
     * Captura cualquier error producido durante la ejecución del componente
     *
     * @param error
     */
    public errorCaptured (error: Error) {
        this.operationError = error.message
        this.saving = false

        // Log del fallo
        // tslint:disable-next-line:max-line-length
        const logMsg = `No se ha podido actualizar el mensaje ${this.message.key}, ha ocurrido un error: ${error.message}`
        Log.error(logMsg, {error, module: 'i18n', message: this.message})
    }
}
